//import { visitResult } from "./WaxeyeHelper.js";
//import exprGrammar from "./syntax.waxeye";
//import {exprGrammar} from "./exprGrammar.js";
import {AST, ParseError, WaxeyeParser} from "../../../lib/waxeye-javascript/waxeye.js";
import {cache, clamp} from "../CoreUtils.js";
import { ExprParser } from "./exprParser.js";
export { ExprParser } from "./exprParser.js";


//export const ExprParser = makeParser(exprGrammar);
/*export const SearchMatcherParser = makeParser(`
matcher <- ws *(clause ws)

clause <- 

${exprGrammar}
`);*/

const lambdaFuncs = new WeakMap();
const LAMBDA = Symbol('LAMBDA');

function repeatChar(c: string, n: number): string {
    const chars = new Array(n);
    chars.fill(`${c}`);
    return chars.join('');
}
export class ExprError extends Error {
    readonly token: {str: string, type: string, start: number, end: number} | null;
    constructor(readonly input: string, msg: string, token: {str: string, type: string, start: number, end: number} | null, cause?: any) {
        super(msg, ...cause ? [{cause}] : []);
        this.token = token;
    }
    prettyPrintPosition(): string {
        if (!this.token) return this.input;
        const lines = this.input.split("\n");
        const out : string[] = [];
        let index = 0;
        for(const line of lines) {
            out.push(line);
            if (index + line.length > this.token.start) {
                out.push(repeatChar(' ', this.token.start - index) + repeatChar('^', clamp(this.token.end - this.token.start, 1, line.length - this.token.start + index)));
            }
            index += line.length + 1;
        }
        return out.join("\n");
    }
}
export class ExprParseError extends ExprError {
    constructor(input: string, p: ParseError) {
        super(input, p.toString(), { str: input.substring(p.pos,  p.pos+25), start: p.pos, end: p.pos, type: p.nt.join("|") }, p);
    }
    toString() {
        return super.toString() + "\n" + this.prettyPrintPosition();
    }
}
export class ExprTransError extends ExprError {
    constructor(input: string, msg: string, token: {str: string, type: string, start: number, end: number} | null, cause?: any) {
        super(input, "ExprTransError: "+msg+(cause?`: ${cause.toString()}`:''), token, cause);
    }
}
export class ExprEvalError extends ExprError{
    constructor(input: string, msg: string, token: {str: string, type: string, start: number, end: number} | null, cause?: any) {
        super(input, "ExprEvalError: "+msg+(cause?`: ${cause.toString()}`:''), token, cause);
    }
    toString() {
        return super.toString() + " at " + this.token?.str + "\n" + this.prettyPrintPosition();
    }
}

function fmtToken(children: any[] | {children:any[]}, type: string, start: number, end: number) {
    return { str: visitResult(children instanceof Array ? {children} : children, BaseExpression.stringifyVisitors, null), type: `${type}`, start, end }
}

export const escapes = { 'n': '\n', 'b': '\b', 'f': '\f', 'r': '\r', 't': '\t', '"': '"', '$': '$', '@': '@', '\\': '\\' };

const promiseAllIfNeeded = (array, then) => {
    if (array.some(x => x instanceof Promise)) {
        return Promise.all(array).then(then);
    } else {
        return then(array);
    }
}
type EvalFun = (ctx: Record<string, PureExpressionResult>) => PureExpressionResult;


export type PureExpressionResult = string | number | {[key: string]: PureExpressionResult} | PureExpressionResult[] | Uint8Array | boolean | null;
export type LambdaExpressionResult = string | number | {[key: string]: LambdaExpressionResult} | LambdaExpressionResult[] | Uint8Array | boolean | null | ((...args: any) => any);
type EvaluatorFn<ResultType> = (ctx: Record<string, ResultType>) => ResultType;
type VisitorType<ResultType> = { [type: string]: (children: any[], type: string, start: number, end: number) => EvaluatorFn<ResultType>; };
export class BaseExpression<ResultType> {
    ast: AST;
    evaluate: EvaluatorFn<ResultType>;
    static parser = ExprParser;

    static stringifyVisitors = {
        '__default__': function(children: any[], type: string, start: number, end: number) {
            return children.map(x => typeof x === 'string' ? x : this.visit(x)).join('');
        },
        'Array': function(children: any[], type: string, start: number, end: number) {
            return `[${children.map(this.visit, this).join(', ')}]`;
        },
        'Object': function(children: any[], type: string, start: number, end: number) {
            return `{${children.map(x => x.children.map(this.visit, this).join(': ')).join(', ')}}`;
        },
        'String': function(children: any[], type: string, start: number, end: number) {
            return `"${children.map(x => typeof x === 'string' ? x : this.visit(x)).join('')}"`;
        },
        'Escaped': function(children: any[], type: string, start: number, end: number) {
            return `\\${children.join('')}`;
        },
        'SubExpr': function(children: any[], type: string, start: number, end: number) {
            return '${' + this.visit(children[0]) + '}';
        },
        'Reference': function(children: any[], type: string, start: number, end: number) {
            return '&' + this.visit(children[0]);
        },
        'SubExprQuiet': function(children: any[], type: string, start: number, end: number) {
            return '@{' + this.visit(children[0]) + '}';
        },
    }
    static lambda(funRef) {
        const fun = lambdaFuncs.get(funRef);
        if (!fun) throw new TypeError("Not a valid lambda");
        return fun;
    }
    constructor(public readonly exprString: string, public visitors: VisitorType<ResultType>, public parser: WaxeyeParser) {
        this.ast = this.parseExpression();
        this.evaluate = this.makeEvaluator();
    }
    parseExpression() {
        try {
            const result = this.parser.parse(this.exprString);
            if (result instanceof ParseError) throw result;
            return result;
        } catch (ex) {
            const pe = new ExprParseError(this.exprString, ex);
            console.error("ParseError in Expression:\n" + pe.prettyPrintPosition() + "\n" + pe.toString());
            throw ex;
        }
    }
    makeEvaluator(): EvaluatorFn<ResultType> {
        try {
            return visitResult<EvaluatorFn<ResultType>>(this.ast, this.visitors, this.exprString);
        } catch(ex) {
            if (!(ex instanceof ExprError)) {
                ex = new ExprTransError(this.exprString, "Unhandled Exception during Expression transformation", null, ex);
            }
            console.error(ex.toString() + "\n" + ex.prettyPrintPosition());
            throw ex;
        }
    }
    toString() {
        return visitResult<string>(this.ast, BaseExpression.stringifyVisitors, null);
    }

	static builtins = {
		'$json': obj => JSON.parse(obj),
		'$toJson': obj => JSON.stringify(obj),
		'$date': obj => { if (!obj) throw "expected date"; return new Date(obj); },
		'$formatInterval': obj => Date.formatInterval(obj),
		'$isset': obj => "",
		'$notempty': obj => { if (!obj) throw "expected non-empty value"; return ""; },
		'$urlencode': obj => encodeURIComponent(obj),
		'$urldecode': obj => decodeURIComponent(obj),
		'$toLocaleTimeString': obj =>  (obj instanceof Date) ? obj.toLocaleTimeString('sv-SV', {hour:'2-digit',minute:'2-digit'}) : obj,
		'$toLocaleDateString': obj => (obj instanceof Date) ? obj.toLocaleDateString('sv-SV') : obj,
		'$toISOString': obj => (obj instanceof Date) ? obj.toISOString() : obj,
		'$split:': param => obj => (''+obj).split(param),
		'$mul:': param => obj => obj * parseFloat(param),
	}
	static getBuiltinMethod(pathEl) {
		if (pathEl[0] !== '$') return;
		let method = this.builtins[pathEl];
		if (method) return method;
		const idx = pathEl.indexOf(':');
		if (idx !== -1) {
			method = this.builtins[pathEl.substring(0, idx)];
			if (method) return method(pathEl.substring(idx + 1));
		}
	}
}
export class Expression extends BaseExpression<PureExpressionResult> {
    static readonly visitors = {
        'Literal': function(children: any[], type: string, start: number, end: number): EvalFun {
            switch (children.join('')) {
                case 'true': return (ctx) => true;
                case 'false': return (ctx) => false;
                case 'null': return (ctx) => null;
                default: throw new ExprTransError(this.input, 'Invalid literal', fmtToken(children, type, start, end));
            }
        },
        'expr': function(children: any[], type: string, start: number, end: number): EvalFun {
            const c = this.visit(children[0]);
            const errToken = fmtToken(children[0], type, start, end);
            return (ctx) => {
                try {
                    return c(ctx);
                } catch(e) {
                    if (e instanceof ExprEvalError) throw e;
                    throw new ExprEvalError(this.input, "Failed to eval", errToken, e);
                }
            };
        },
        'SubExpr': function(children: any[], type: string, start: number, end: number): EvalFun {
            const c = this.visit(children[0]);
            const errToken = fmtToken(children[0], type, start, end);
            return (ctx) => {
                try {
                    return c(ctx);
                } catch(e) {
                    if (e instanceof ExprEvalError) throw e;
                    throw new ExprEvalError(this.input, "Failed to eval", errToken, e);
                }
            };
        },
        'SubExprQuiet': function(children: any[], type: string, start: number, end: number): EvalFun {
            const c = this.visit(children[0]);
            return (ctx) => {
                try {
                    const result = c(ctx);
                    if (result instanceof Promise) return result.catch(() => "");
                    return result;
                } catch(e) {
                    return "";
                }
            };
        },
        'Number': function(children: any[], type: string, start: number, end: number): EvalFun {
            return (ctx) => parseFloat(children.join(''));
        },
        'String': function(children: any[], type: string, start: number, end: number): EvalFun {
            const r: any[] = [];
            for (const el of children) {
                if (typeof el === 'string') {
                    r.push(el);
                } else if (el.type === 'Escaped') {
                    switch (el.children.length) {
                        case 1:
                            r.push(escapes[el.children[0]]);
                            break;
                        case 3:
                            if (el.children[0] === 'x') r.push(String.fromCharCode(parseInt(el.children[1] + el.children[2], 16)));
                            break;
                    }
                } else {
                    r.push(this.visit(el));
                }
            }
            return (ctx) => promiseAllIfNeeded(r.map(x => typeof x === 'string' ? x : x(ctx)), r => r.join(''));
        },
        'Array': function(children: any[], type: string, start: number, end: number): EvalFun {
            let n = children.map(x => this.visit(x));
            return (ctx) => n.map(x => x(ctx));
        },
        'Object': function(children: any[], type: string, start: number, end: number): EvalFun {
            let n = children.map(x => [this.visit(x.children[0]), this.visit(x.children[1])]);
            return (ctx) => Object.fromEntries(n.map(x => [x[0](ctx), x[1](ctx)]));
        },
        'and_expr': function(children: any[], type: string, start: number, end: number): EvalFun {
            let n = [this.visit(children[0])];
            for(let i = 1; i < children.length; i++) {
                n.push(this.visit(children[i]));
            }
            return (ctx) => n.reduce((acc, x) => acc && x(ctx), true);
        },
        'or_expr': function(children: any[], type: string, start: number, end: number): EvalFun {
            let n = [this.visit(children[0])];
            for(let i = 1; i < children.length; i++) {
                n.push(this.visit(children[i]));
            }
            return (ctx) => n.reduce((acc, x) => acc || x(ctx), 0);
        },
        'sum': function(children: any[], type: string, start: number, end: number): EvalFun {
            let n = [[1, this.visit(children[0])]];
            for(let i = 1; i < children.length; i+=2) {
                n.push([children[i] === '-' ? -1 : 1, this.visit(children[i+1])]);
            }
            return (ctx) => n.reduce((acc, x) => acc + x[0] * x[1](ctx), 0);
        },
        'prod': function(children: any[], type: string, start: number, end: number): EvalFun {
            let n = [['*', this.visit(children[0])]];
            for(let i = 1; i < children.length; i+=2) {
                n.push([children[i], this.visit(children[i+1])]);
            }
            return (ctx) => n.reduce((acc, x) => (x[0] === '*') ? (acc * x[1](ctx)) : (acc / x[1](ctx)), 1);
        },
        'ident': function(children: any[], type: string, start: number, end: number): EvalFun {
            const ident = children.join('');
            if (ident === '$this')
                return (ctx) => ctx;
            else
                return (ctx) => ctx[ident];
        },
        'index': function(children: any[], type: string, start: number, end: number): EvalFun {
            let r = [this.visit(children[0])];
            for(let n = 1; n < children.length; n++) {
                if (children[n] === '?') {
                    n++; r.push('?');
                }
                const errToken = fmtToken(children.slice(0, n), type, start, children[n-1].end ?? end);
                r.push(o => {
                    if (typeof o === 'undefined' || o === null) throw new ExprEvalError(this.input, 'Not found', errToken);
                    return o;
                })
                if (children[n] === '.') {
                    n++;
                    const pathEl = children[n].children.join('');
                    const method = Expression.getBuiltinMethod(pathEl);
                    if (method)
                        r.push(method);
                    else if (pathEl[0] === '$' && children[n+1] === ':') {
                        if (pathEl === '$loop') {
                            n++; n++;
                            const joiner = this.visit(children[n]);
                            n++;
                            const rest = Expression.visitors.index.call(this, [{children:['_'],type:'ident'}].concat(children.slice(n)), null, null, null);
                            r.push((o, ctx) => [...o].map(_ => rest({_})).join(joiner(ctx)));
                            break;
                        } else if (pathEl === '$map') {
                            n++;
                            n++;
                            const lambda = this.visit(children[n]);
                            r.push((o, ctx) => [...o].map(lambda));
                        } else {
                            n++;
                            n++;
                            const method = Expression.builtins[pathEl + ':'];
                            if (method) {
                                //const curried = method(this.visit(children[n])(null));
                                //r.push(curried);
                                const paramExpr = this.visit(children[n]);
                                r.push((o, ctx) => method(paramExpr(ctx))(o));
                            } else {
                                throw new ExprTransError(this.input, "Unknown method", fmtToken(children.slice(0, n), type, start, end));
                            }
                        }
                    } else
                        r.push(o => o[pathEl]);
                } else if (children[n] === '[') {
                    n++;
                    const idx = this.visit(children[n]);
                    r.push((o, ctx) => o[idx(ctx)]);
                    n++;
                }
            }
            return (ctx) => {
                let acc = ctx;
                for (const cur of r) {
                    if (cur === '?'){
                        if (typeof acc === 'undefined' || acc === null) return acc;
                    } else {
                        acc = cur(acc, ctx);
                    }
                }
                return acc;
            };
        },
        'unary': function(children: any[], type: string, start: number, end: number): EvalFun {
            if (children[0] === '-') {
                const v = this.visit(children[1]);
                return (ctx) => -v(ctx);
            } else {
                throw new ExprTransError(this.input, 'Invalid unary', fmtToken(children, type, start, end));
            }
        },
        'paren': function(children: any[], type: string, start: number, end: number): EvalFun {
            if (children[0] === '(') {
                return this.visit(children[1]);
            } else {
                throw new ExprTransError(this.input, 'Invalid paren', fmtToken(children, type, start, end));
            }
        },
    };

    constructor(exprString: string) {
        super(exprString, Expression.visitors, BaseExpression.parser);
    }
    //@cache
    get asReference() {
        try {
            return new RefExpression('&' + this.exprString);
        } catch (e) {
            console.log("not a valid lhs:",this.exprString,e);
            throw e;
        }
    }
    static objectToString(obj) {
        return JSON.stringify(obj).replace(/\$/g, "\\$").replace(/@/g, "\\@");
    }
}
export class BaseFnExpression extends BaseExpression<LambdaExpressionResult> {
}
export class LambdaExpression extends BaseFnExpression {
    static readonly visitors = Object.assign(Object.create(Expression.visitors), {
        'Lambda': function(children: any[], type: string, start: number, end: number): EvalFun {
            const v = this.visit(children[0]);
            return (ctx) => this.mkLambda((params) => v(Object.assign(Object.create(ctx), params, {$_: params})));
        },
    });
    constructor(exprString: string) {
        super(exprString, LambdaExpression.visitors, BaseExpression.parser)
    }
}
export class RefExpression extends BaseFnExpression {
    static readonly visitors = Object.assign(Object.create(LambdaExpression.visitors), {
        'lhs': function(children: any[], type: string, start: number, end: number): (ctx) => Function {
            let r = [children.length === 1 ? (o, _, v) => o[children[0].children.join('')] = v : this.visit(children[0])];
            for(let n = 1; n < children.length; n++) {
                if (children[n] === '?') {
                    n++; if (n === children.length) break; else r.push('?');
                }
                const errMes = this.visitResult({children:children.slice(0, n)}, BaseExpression.stringifyVisitors) + ' not found';
                r.push(o => {
                    if (typeof o === 'undefined' || o === null) throw errMes;
                    return o;
                })
                if (children[n] === '.') {
                    n++;
                    const pathEl = children[n].children.join('');
                    const method = Expression.getBuiltinMethod(pathEl);
                    if (method)
                        r.push(method);
                    else if (pathEl[0] === '$' && children[n+1] === ':') {
                        throw "methods not supported on lhs"
                    } else
                        r.push(n + 1 == children.length ? (o, _, v) => o[pathEl] = v : o => o[pathEl]);
                } else if (children[n] === '[') {
                    n++;
                    const idx = this.visit(children[n]);
                    r.push(n + 2 == children.length ? (o, ctx, v) => o[idx(ctx)] = v : (o, ctx) => o[idx(ctx)]);
                    n++;
                }
            }
            return (ctx) => (newValue) => {
                let acc = ctx;
                for(let n = 0; n < r.length; n++) {
                    if (r[n] === '?'){
                        if (typeof acc === 'undefined' || acc === null) return;
                    } else if (n + 1 >= r.length) {
                        r[n](acc, ctx, newValue);
                    } else {
                        acc = r[n](acc, ctx);
                    }
                }
            };
        },
        'Reference': function(children: any[], type: string, start: number, end: number): EvalFun {
            return this.visit(children[0]);
        },
    });
    constructor(public readonly exprString: string) {
        super(exprString, RefExpression.visitors, BaseExpression.parser)
    }
}

export function transform(exprString: string): Function {
    return visitResult<Function>(ExprParser.parse(exprString), Expression.visitors, exprString);
}

type VisitorSelf = { input: string | null, visit?: (item: any) => any, visitResult: any, mkLambda(fn: Function): { [LAMBDA]: any } };
export function visitResult<T>(parseResult, visitors: {[type: string]: (children: any[], type: string, start: number, end: number) => T}, inputStr: string | null): T {
    const self : VisitorSelf = {
        mkLambda: (fn: Function) => {
            const ref = { [LAMBDA]: true };
            lambdaFuncs.set(ref, fn);
            return ref;
        },
        visit: (item) => {
            const fn = visitors[item.type] || visitors['__default__'];
            if (!fn) throw "no visitor defined for type "+item.type;
            return fn.call(self, item.children, item.type, item.start, item.end);
        },
        visitResult,
        input: inputStr,
    };
    const result = self.visit!!(parseResult);
    self.visit = undefined;
    return result;
}
